import { Button, IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { cssThemedValue, elevationCss, typographyCss, colors } from '@creditornot/cb-ingredients';
import React, { ReactNode, useCallback, useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';

import { usePrevious } from 'common/hooks';

import { MENU_MOBILE_BREAKPOINT } from '../constants';

type RenderProps = {
  editing: boolean;
  setEditing: (state: boolean) => void;
};

type Props = {
  id: string;
  thumbnail?: ReactNode;
  title: string;
  children: ReactNode | ((renderProps: RenderProps) => ReactNode);
  onClose: () => void;
};

const Sidepanel = styled.div`
  max-width: 640px;
  min-width: 480px;
  background-color: ${cssThemedValue('surfaceMain')};
  border-inline-start: 1px solid ${cssThemedValue('borderPrimary')};
  overflow: auto;
  z-index: 1;
  position: relative;
  color: ${cssThemedValue('textPrimary')};

  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    min-width: 320px;
  }

  /*** scrollbar customization ***/

  /* Firefox */
  scrollbar-width: thin;
  /* ignore-color */
  scrollbar-color: ${colors.pepper32} transparent;

  /* Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${cssThemedValue({ light: colors.pepper32, dark: colors.salt36 })};
    border-radius: 20px;
    border: 3px solid ${({ theme }) => theme.cbSemanticColors.surfaceMain};
  }
`;

const Header = styled.div<{ isScrollTop: boolean }>`
  ${({ isScrollTop }) => elevationCss(isScrollTop ? 0 : 1)};
  position: sticky;
  top: 0;
  height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-inline-start: 24px;
  padding-inline-end: 24px;
  background-color: ${cssThemedValue('surfaceMain')};
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  z-index: 1;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
  }
`;

const ImgContainer = styled.div`
  margin-inline-end: 16px;
  flex-shrink: 0;
`;

const TitleContainer = styled.div`
  flex: 1;
  min-width: 0;
  margin-inline-end: 16px;
`;

const Title = styled.div`
  ${typographyCss.Heading6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MenuEditorSidepanel = ({ id, thumbnail, title, children, onClose }: Props) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const [isScrollTop, setIsScrollTop] = useState(true);
  const [editing, setEditing] = useState(false);

  const handleScroll = useCallback(
    (event: React.UIEvent) => {
      const isCurrentScrollTop = event.currentTarget.scrollTop === 0;
      if (isCurrentScrollTop !== isScrollTop) {
        setIsScrollTop(isCurrentScrollTop);
      }
    },
    [isScrollTop],
  );

  const previousId = usePrevious(id);

  useEffect(() => {
    if (previousId !== id) {
      panelRef.current?.scrollTo({ top: 0 });
      setEditing(false);
    }
  }, [id, previousId]);

  return (
    <AutoSizer disableWidth>
      {({ height }) => (
        <Sidepanel style={{ height }} onScroll={handleScroll} ref={panelRef}>
          <Header isScrollTop={isScrollTop}>
            {thumbnail && <ImgContainer>{thumbnail}</ImgContainer>}
            <TitleContainer title={title}>
              <Title>{title}</Title>
            </TitleContainer>
            <ButtonsContainer>
              {typeof children === 'function' && editing === false && (
                <Button
                  style={{ marginInlineEnd: 16 }}
                  variant="lightBlue"
                  size="small"
                  onClick={() => setEditing(true)}
                >
                  <FormattedMessage id="state.edit" />
                </Button>
              )}
              <IconButton size="small" variant="grey" onClick={onClose}>
                <Cross />
              </IconButton>
            </ButtonsContainer>
          </Header>
          {typeof children === 'function' ? children({ editing, setEditing }) : children}
        </Sidepanel>
      )}
    </AutoSizer>
  );
};

export default MenuEditorSidepanel;
